import { useState, useMemo, useEffect, type ElementType } from 'react'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { ExternalLink, Tags, PieChart as PieIcon, AlertTriangle, Table2, Layers, Bug, CheckCircle2 } from 'lucide-react'
import type { JiraSnapshot, JiraIssue } from '../types'
import { type JiraFilter, countByType, countByLabel, buildTypeColors, normalisePriority, statusBreakdown } from '../lib/jira'
import { ChartCard } from './ChartCard'

const TIP = { fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,.08)' }
const BLUE = '#005EB8'
const RED = '#EF3340'
const AMBER = '#F88806'
const GREEN = '#54AD18'
// Keep the DOM bounded - the default view (previous month) is small, but a
// cleared filter could list thousands. Surfaced in the table header, never silent.
const MAX_ROWS = 500

// Compact stat tile - matches the ServiceNow tiles on the Operational page so
// the two source views feel like one tool.
function Tile({ icon: Icon, iconBg, label, value, sub }: {
  icon: ElementType; iconBg: string; label: string; value: number; sub?: string
}) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: iconBg }}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-0.5">{label}</div>
        <div className="text-2xl font-bold text-slate-800 leading-none">{value.toLocaleString()}</div>
        {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  )
}

// Presentational: the filter state + controls live on the Operational page (in
// the shared data-source box), mirroring ServiceNow. This renders the content:
// headline tiles, the request-type pie, product/label counts, and the table.
export default function JiraPanel({ snapshot, filtered, filter, onToggleLabel }: {
  snapshot: JiraSnapshot
  filtered: JiraIssue[]
  filter: JiraFilter
  onToggleLabel: (label: string) => void
}) {
  const [typeFromPie, setTypeFromPie] = useState<string | null>(null)
  // The pie click is a table-only drill-down; clear it whenever the page-level
  // filter changes so a stale type can't hide the table.
  useEffect(() => { setTypeFromPie(null) }, [filter])

  const typeCounts = useMemo(() => countByType(filtered), [filtered])
  const labelCounts = useMemo(() => countByLabel(filtered), [filtered])
  const typeColor = useMemo(() => buildTypeColors(typeCounts.map(t => t.type)), [typeCounts])

  // Headline figures for the tiles. Priority is normalised so P1/P2 counts work
  // whatever scheme the board uses.
  const summary = useMemo(
    () => ({
      total: filtered.length,
      p1: filtered.filter(i => normalisePriority(i.priority) === 'P1').length,
      p2: filtered.filter(i => normalisePriority(i.priority) === 'P2').length,
      bugs: filtered.filter(i => i.type === 'Bug').length,
    }),
    [filtered],
  )

  const statusCounts = useMemo(() => statusBreakdown(filtered), [filtered])

  const tableIssues = typeFromPie ? filtered.filter(i => i.type === typeFromPie) : filtered
  const rows = tableIssues.slice(0, MAX_ROWS)
  const maxLabel = labelCounts[0]?.count ?? 1
  const shownLabels = labelCounts.slice(0, 12)

  return (
    <div className="space-y-4">
      {snapshot.droppedForSize ? (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          Showing the {snapshot.storedCount.toLocaleString()} most recent issues; {snapshot.droppedForSize.toLocaleString()} older ones were trimmed to fit.
        </div>
      ) : null}

      {/* Headline figures - reflect the current filter */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile icon={Layers} iconBg={BLUE} label="Total Issues" value={summary.total} sub="in current filter" />
        <Tile icon={AlertTriangle} iconBg={summary.p1 > 0 ? RED : GREEN} label="P1" value={summary.p1} sub="highest priority" />
        <Tile icon={AlertTriangle} iconBg={summary.p2 > 0 ? AMBER : GREEN} label="P2" value={summary.p2} sub="high priority" />
        <Tile icon={Bug} iconBg={RED} label="Bugs" value={summary.bugs} sub="bug-type issues" />
      </div>

      {/* Request type pie + product/label counts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          icon={PieIcon}
          iconGrad="from-[#005EB8] to-[#005EB8] text-white"
          title="Request Type"
          subtitle={typeFromPie ? `Filtered to ${typeFromPie} - click the slice again to clear` : 'Click a slice to filter the table below'}
          bodyClass="flex-1 flex flex-col justify-center"
        >
          {typeCounts.length === 0 ? (
            <div className="h-[260px] flex items-center justify-center text-sm text-slate-400">No issues in view</div>
          ) : (
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                  <Pie
                    data={typeCounts}
                    dataKey="count"
                    nameKey="type"
                    cx="42%"
                    cy="50%"
                    innerRadius="52%"
                    outerRadius="92%"
                    paddingAngle={2}
                    onClick={(_, idx) => {
                      const t = typeCounts[idx]?.type
                      if (t) setTypeFromPie(p => (p === t ? null : t))
                    }}
                  >
                    {typeCounts.map(t => (
                      <Cell
                        key={t.type}
                        fill={typeColor.get(t.type)}
                        opacity={typeFromPie && typeFromPie !== t.type ? 0.3 : 1}
                        cursor="pointer"
                      />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TIP} formatter={(v: any, n: any) => [v, n]} />
                  <Legend
                    layout="vertical"
                    verticalAlign="middle"
                    align="right"
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        <ChartCard
          icon={Tags}
          iconGrad="from-[#54AD18] to-[#54AD18] text-white"
          title="By Product / Label"
          subtitle={labelCounts.length > 0 ? `${labelCounts.length} label${labelCounts.length === 1 ? '' : 's'} in view - click to filter` : 'Labels on the issues in view'}
        >
          <div className="space-y-2.5 mt-1">
            {shownLabels.map(({ label, count }) => {
              const active = filter.labels.includes(label)
              return (
                <button key={label} onClick={() => onToggleLabel(label)} className="w-full text-left group">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-medium truncate max-w-[220px] ${active ? 'text-[#005EB8]' : 'text-slate-700 group-hover:text-[#005EB8]'}`}>{label}</span>
                    <span className="text-xs font-bold text-slate-600 ml-2 flex-shrink-0">{count}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100">
                    <div className="h-1.5 rounded-full" style={{ width: `${(count / maxLabel) * 100}%`, background: GREEN }} />
                  </div>
                </button>
              )
            })}
            {labelCounts.length === 0 && <div className="text-sm text-slate-400">No labels on these issues</div>}
            {labelCounts.length > shownLabels.length && (
              <div className="text-xs text-slate-400 pt-1">+{labelCounts.length - shownLabels.length} more - use the Label filter</div>
            )}
          </div>
        </ChartCard>
      </div>

      {/* By Status - work state across the filter */}
      <ChartCard
        icon={CheckCircle2}
        iconGrad="from-[#54AD18] to-[#54AD18] text-white"
        title="By Status"
        subtitle="Work state across the current filter"
      >
        {(() => {
          const total = statusCounts.todo + statusCounts.inProgress + statusCounts.done
          const seg = [
            { label: 'To Do', count: statusCounts.todo, color: '#94a3b8' },
            { label: 'In Progress', count: statusCounts.inProgress, color: AMBER },
            { label: 'Done', count: statusCounts.done, color: GREEN },
          ]
          const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0)
          return (
            <div className="mt-1">
              <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
                {seg.filter(x => x.count > 0).map(x => (
                  <div key={x.label} title={`${x.label}: ${x.count}`} style={{ width: `${(x.count / total) * 100}%`, background: x.color }} />
                ))}
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3">
                {seg.map(x => (
                  <div key={x.label} className="flex items-center gap-2 text-xs">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: x.color }} />
                    <span className="font-medium text-slate-700">{x.label}</span>
                    <span className="font-bold text-slate-800">{x.count.toLocaleString()}</span>
                    <span className="text-slate-400">{pct(x.count)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )
        })()}
      </ChartCard>

      {/* Issues table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl flex items-center justify-center flex-shrink-0">
              <Table2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800">Issues</h3>
              <p className="text-xs text-slate-400">Click an issue key to open it in Jira</p>
            </div>
            {typeFromPie && (
              <button
                onClick={() => setTypeFromPie(null)}
                className="ml-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 hover:bg-slate-200"
              >
                Type: {typeFromPie} ✕
              </button>
            )}
          </div>
          <span className="text-xs text-slate-400">
            {tableIssues.length.toLocaleString()} shown{tableIssues.length > MAX_ROWS ? ` (first ${MAX_ROWS})` : ''}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-4 font-semibold">Key</th>
                <th className="py-2 pr-4 font-semibold">Created</th>
                <th className="py-2 pr-4 font-semibold">Type</th>
                <th className="py-2 pr-4 font-semibold">Summary</th>
                <th className="py-2 pr-4 font-semibold">Assignee</th>
                <th className="py-2 pr-4 font-semibold">Reporter</th>
                <th className="py-2 font-semibold">Priority</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(i => (
                <tr key={i.key} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-2 pr-4">
                    <a
                      href={`${snapshot.browseBaseUrl}/${i.key}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#005EB8] font-semibold hover:underline inline-flex items-center gap-1 whitespace-nowrap"
                    >
                      {i.key}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                  <td className="pr-4 text-slate-500 whitespace-nowrap">{i.created ?? '-'}</td>
                  <td className="pr-4 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5 text-slate-700">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: typeColor.get(i.type) ?? '#94a3b8' }} />
                      {i.type}
                    </span>
                  </td>
                  <td className="pr-4 text-slate-700 max-w-[380px] truncate" title={i.summary}>{i.summary}</td>
                  <td className="pr-4 text-slate-600 whitespace-nowrap">{i.assignee ?? '-'}</td>
                  <td className="pr-4 text-slate-600 whitespace-nowrap">{i.reporter ?? '-'}</td>
                  <td className="text-slate-600 whitespace-nowrap">{i.priority ?? '-'}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-400">No issues match these filters</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
